import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminProductDetailsForm } from '../../models/admin-product-details-form';
import { AdminProductDataSourceService } from '../../services/admin-product-data-source.service';
import { AdminProductDetailsFormComponent } from '../admin-product-details-form/admin-product-details-form.component';

@Component({
  selector: 'app-admin-product-edit',
  imports: [
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
    AdminProductDetailsFormComponent,
  ],
  templateUrl: './admin-product-edit.component.html',
  styleUrl: './admin-product-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminProductEditComponent {
  private readonly productDataSource = inject(AdminProductDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminProductDetailsForm): void {
    this.productDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.notifySubmitSuccessEvent(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminProductDetailsForm> {
    return this.id$.pipe(
      switchMap((id) => this.productDataSource.fetchDetailsFormById(id)),
    );
  }
}
